import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../lib/api';
import { Mail, CheckCircle } from 'lucide-react';

export default function VerifyEmail() {
  const [isResending, setIsResending] = useState(false);
  const [isAlreadyVerified] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { success, error } = useToast();

  useEffect(() => {
    const run = async () => {
      const token = new URLSearchParams(location.search).get('token');
      if (token) {
        try {
          await api.post('/auth/verify', { token });
          navigate('/auth/login?verified=1', { replace: true });
        } catch {
          navigate('/auth/login?verified=0', { replace: true });
        }
      }
    };
    run();
  }, [location.search, navigate]);

  // Get email from location state (passed from registration)
  const email = location.state?.email || 'your email';

  const handleResendEmail = async () => {
    if (isResending) return;

    setIsResending(true);

    try {
      await api.post('/auth/request-verify-token', {
        email: typeof email === 'string' ? email : '',
      });

      success('Verification link sent!', {
        description: 'Please check your email for the verification link.',
      });
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      error('Failed to resend link', {
        description: 'Please try again in a few moments.',
      });
    } finally {
      setIsResending(false);
    }
  };

  // Check if email is already verified (from location state)
  if (location.state?.alreadyVerified || isAlreadyVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linen px-4 py-8">
        <Card className="w-full max-w-md p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-success rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-success-dark" />
            </div>
            <h1 className="font-display text-3xl font-bold text-blueviolet mb-2">
              Already Verified
            </h1>
            <p className="font-body text-night-500">Your email address is already verified.</p>
          </div>

          {/* Action Button */}
          <div className="space-y-4">
            <Button onClick={() => navigate('/auth/login')} className="w-full">
              Continue to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linen px-4 py-8">
      <Card className="w-full max-w-md p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-lilac-900 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-blueviolet" />
          </div>
          <h1 className="font-display text-3xl font-bold text-blueviolet mb-2">Verify Your Email</h1>
          <p className="font-body text-night-500">We've sent a verification link to</p>
          <p className="font-body text-sm font-medium text-blueviolet mt-1">{email}</p>
          <p className="font-body text-night-500 mt-4">
            Click the link in your email to verify your account.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <Button
            onClick={handleResendEmail}
            disabled={isResending}
            variant="outline"
            className="w-full"
          >
            {isResending ? 'Sending...' : 'Resend Link'}
          </Button>

          <div className="text-center">
            <p className="font-body text-sm text-night-500">
              Wrong email address?{' '}
              <Link
                to="/auth/register"
                className="font-medium text-blueviolet hover:text-blueviolet-light underline"
              >
                Try again
              </Link>
            </p>
          </div>

          <div className="text-center">
            <p className="font-body text-sm text-night-500">
              Already have an account?{' '}
              <Link
                to="/auth/login"
                className="font-medium text-blueviolet hover:text-blueviolet-light underline"
              >
                Log in
              </Link>
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
